import {
  Button,
  H5,
  H6,
  InputField,
  InputText,
  InputTextArea,
  InputCheckbox,
} from "@sellar-io/components";
import { ReactElement, useState } from "react";
import { useDebounce } from "use-debounce";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import { Icon, ProfilePicture, SpinnerLegacy } from "../../../components";
import { Switch } from "@sellar-io/components";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useBusinessUserList } from "../../../hooks/api/v2/businessUser/useBusinessUserList";
import {
  SellarContextActionEnum,
  useSellarContext,
  useSellarUser,
} from "../../../hooks";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";
import { useBusinessList } from "../../../hooks/api/v2/business/useBusinessList";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";
import { useIntercom } from "react-use-intercom";
import { useLegalEntityUpdate } from "../../../hooks/api/v2/legalEntity/useLegalEntityUpdate";
import { useBusinessUserUpdate } from "../../../hooks/api/v2/businessUser/useBusinessUserUpdate";
import { LegalEntitiesGetResponseType } from "@sellar-io/api-v2/types";
import { useLegalEntity } from "../../../hooks/api/v2/legalEntity/useLegalEntity";
import { Alert } from "../../../components/alert/Alert";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../../components/table/TableLegacy";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { PageCollectionLayout } from "../../../components/pageCollection/PageCollectionLayout";
import { twMerge } from "tailwind-merge";
import { Settings } from "../constants/tabs";
import { tabsLeft } from "../constants/tabs.shared";
import { useMediaQuery } from "usehooks-ts";
import { useLegalEntityId } from "../../../hooks/api/v2/legalEntity/useLegalEntityId";
import { DISCOVER } from "../../../constants/routes";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { useSearchParams } from "react-router";
import { useLegalEntityUpdateInvoiceCcEmails } from "~/hooks/api/v2/legalEntity/useLegalEntityUpdateInvoiceCCEmails";
import { useForm } from "@tanstack/react-form";
import { isValidEmail } from "@sellar-io/common";
import { useFeatureFlagStore } from "~/hooks/useFeatureFlags";
import z from "zod";

export const ManageCompanySettings = ({
  sellarCreditTabView,
}: {
  sellarCreditTabView: boolean;
}): ReactElement | null => {
  const [searchParams] = useSearchParams();
  const provider = searchParams.get("provider") === "hokodo" ? "hokodo" : "two";
  const { business: currentBusiness } = useSellarCurrentBusiness();
  const legalEntityId = useLegalEntityId();
  const legalEntityResponse = useLegalEntity(legalEntityId, {
    enabled: !!legalEntityId,
  });
  const scPaymentAllocationHistoryEnabled = useFeatureFlagStore().isActive(
    "SELLAR_CREDIT_PAYMENT_ALLOCATION_HISTORY",
  );
  const legalEntity = legalEntityResponse.data;

  const isDesktop = useMediaQuery("(min-width: 1200px)");

  if (legalEntityResponse.isPending) {
    return <SpinnerLegacy />;
  }

  // No legal entity found associated with the user

  if (!legalEntity || !currentBusiness) {
    return <div>No company found or current business found</div>;
  }

  // check if current business is included in legalEntity list, if not return the Attach

  const isCurrentBusinessAttachedToLegalEntity = legalEntity.businessList.find(
    (business) => business.id === currentBusiness.id,
  );

  if (!isCurrentBusinessAttachedToLegalEntity) {
    return (
      <AttachBusiness
        legalEntity={legalEntity}
        currentBusinessId={currentBusiness.id}
      />
    );
  }

  if (sellarCreditTabView) {
    return (
      <PageCollectionLayout
        title="Sellar Credit"
        tabsLeft={tabsLeft({ provider, scPaymentAllocationHistoryEnabled })}
        rightComponent={<Settings provider={provider} />}
      >
        <div
          className={twMerge(
            "container mx-auto max-w-[1300px] bg-zinc-100",
            isDesktop ? "mt-6" : "mt-0",
          )}
        >
          <Content legalEntity={legalEntity} />
        </div>
      </PageCollectionLayout>
    );
  }

  return (
    <>
      <NavigationBar title={"Manage company"} leftIcon={"CHEVRON_LEFT"} />
      <div
        className={twMerge(
          "container mx-auto max-w-[1300px] bg-zinc-100",
          isDesktop ? "mt-6" : "mt-0",
        )}
      >
        <Content legalEntity={legalEntity} />
      </div>
    </>
  );
};

const Content = ({
  legalEntity,
}: {
  legalEntity: LegalEntitiesGetResponseType;
}) => {
  return (
    <>
      <CompanyDetails
        legalEntityId={legalEntity.id}
        legalEntityAdminUserName={
          legalEntity.legalEntityAdminUserList[0]?.name ?? "Unknown"
        }
        legalEntityName={legalEntity.name}
        legalEntityBusinessList={legalEntity.businessList}
      />
      <InvoiceEmailDetails
        legalEntityId={legalEntity.id}
        invoiceCcEmails={legalEntity.invoiceCcEmails}
        legalEntityAdminEmail={
          legalEntity.legalEntityAdminUserList[0]?.email ?? ""
        }
      />
      <SellarCreditAccess />
    </>
  );
};

const AttachBusiness = ({
  legalEntity,
  currentBusinessId,
}: {
  legalEntity: LegalEntitiesGetResponseType;
  currentBusinessId: number;
}) => {
  const [ownershipConfirmation, setOwnershipConfirmation] = useState(false);
  const legalEntityUpdateMutation = useLegalEntityUpdate();
  const onUpdateBusinessesAttachedToLegalEntity = () => {
    legalEntityUpdateMutation
      .mutateAsync({
        businessIdList: [
          ...legalEntity.businessList.map((business) => business.id),
          currentBusinessId,
        ],
      })
      .catch((_error) => {
        console.error("Error updating legal entity attached businesses");
      });

    // full page refresh - ensures that business attaches to legal entity
    window.location.replace(`${window.location.origin}${DISCOVER}`);
  };

  return (
    <PageContainer className={"mx-auto my-4 max-w-[1200px]"}>
      <div className="mx-auto flex flex-col items-center gap-8 px-4 py-8">
        <div className="flex flex-col items-center gap-2">
          <div className="font-semibold text-zinc-700">
            This business is not attached to the Company you have registered on
            Sellar
          </div>
          <div className="text-zinc-700">
            You can attach this business to your company using the button below
          </div>
        </div>

        <label className="flex w-full cursor-pointer gap-4 rounded-lg border border-indigo-600 bg-indigo-50 p-4 text-left">
          <InputCheckbox
            variant="circular"
            checked={ownershipConfirmation}
            onChange={(event) => setOwnershipConfirmation(event.target.checked)}
            containerClassName="flex-none"
          />
          <p className="break-words">
            <p className="font-semibold">Ownership confirmation</p>I confirm
            that the businesses selected operate under the {legalEntity.name}{" "}
            registered Company name and take full responsibility for their usage
            of Sellar Credit
          </p>
        </label>

        <Button
          onClick={() => onUpdateBusinessesAttachedToLegalEntity()}
          disabled={
            !ownershipConfirmation || legalEntityUpdateMutation.isPending
          }
        >
          ATTACH BUSINESS
        </Button>
      </div>
    </PageContainer>
  );
};

const CompanyDetails = ({
  legalEntityId,
  legalEntityAdminUserName,
  legalEntityName,
  legalEntityBusinessList,
}: {
  legalEntityId: number;
  legalEntityAdminUserName: string;
  legalEntityName: string;
  legalEntityBusinessList: { id: number; name: string }[];
}) => {
  const [manageBusinessesDialogActive, setManageBusinessesDialogActive] =
    useState(false);
  const [contactSupportDialogActive, setContactSupportDialogActive] =
    useState(false);

  const formatter = new Intl.ListFormat("en", {
    style: "long",
    type: "conjunction",
  });

  return (
    <>
      <PageContainer className={"mx-auto max-w-[1200px]"}>
        <div className="mx-auto px-4 py-5">
          <div className="flex items-center justify-between gap-2">
            <div>
              <H5>Company Details</H5>
              <div>
                You can manage which businesses on Sellar are owned by your
                company
              </div>
            </div>

            <Button
              color="secondary"
              className="flex min-w-fit shrink-0 gap-2"
              onClick={() => setManageBusinessesDialogActive(true)}
            >
              <Icon size={0.7} icon={"COG"} />
              <div>Manage Businesses</div>
            </Button>
          </div>
        </div>

        <div className="border-b border-zinc-300" />

        <div className="mx-auto px-4 py-5">
          <table className="w-full table-auto border-separate border-spacing-0 rounded-lg border border-zinc-300">
            <tr>
              <td className="border-r border-b border-zinc-300 px-4 py-3 md:w-[200px]">
                <H5>Company Name</H5>
              </td>
              <td className="border-b border-zinc-300 px-4 py-3">
                {legalEntityName}
              </td>
            </tr>
            <tr>
              <td className="border-r border-b border-zinc-300 px-4 py-3">
                <H5>Company Director</H5>
              </td>
              <td className="border-b border-zinc-300 px-4 py-3">
                {legalEntityAdminUserName}
              </td>
            </tr>
            <tr>
              <td className="border-r border-zinc-300 px-4 py-3">
                <H5>Owned Businesses</H5>
              </td>
              <td className="border-zinc-300 px-4 py-3">
                {formatter.format(
                  legalEntityBusinessList.map((business) => business.name),
                )}
              </td>
            </tr>
          </table>
        </div>
      </PageContainer>
      <ManageBusinessesDialog
        active={manageBusinessesDialogActive}
        legalEntity={{ id: legalEntityId, name: legalEntityName }}
        businessesAttachedToLegalEntity={legalEntityBusinessList.map(
          (business) => business.id,
        )}
        onDismiss={() => setManageBusinessesDialogActive(false)}
        onClickContactSupport={() => {
          setManageBusinessesDialogActive(false);
          setContactSupportDialogActive(true);
        }}
      />
      <ContactSupportDialog
        active={contactSupportDialogActive}
        onDismiss={() => setContactSupportDialogActive(false)}
      />
    </>
  );
};

const SellarCreditAccess = () => {
  const businessUserListQuery = useBusinessUserList({});
  const user = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const [, dispatch] = useSellarContext();
  const businessName = business?.name;
  const currentBusinessUser = businessUserListQuery.data?.pages[0]?.find(
    (bu) => bu.User.id === user.user?.id,
  );
  const allOtherUsers =
    businessUserListQuery.data?.pages[0]?.filter(
      (bu) => bu.User.id !== user.user?.id,
    ) ?? [];

  const handleOnClickSwitcher = () => {
    dispatch({
      type: SellarContextActionEnum.CHANGE_BUSINESS_SWITCHER_ACTIVE,
      businessSwitcherActive: true,
    });
  };

  return (
    <PageContainer className={"mx-auto mt-4 mb-[10vh] max-w-[1200px] pb-5"}>
      <div className="mx-auto px-4 py-5">
        <H5>Sellar Credit: {businessName}</H5>

        <div className="flex items-center justify-between gap-2">
          <div>
            Control which users have access to your Sellar Credit limit. You can
            switch between your owned businesses using the button opposite.
          </div>

          <Button
            color="secondary"
            className="flex min-w-fit shrink-0 gap-2"
            onClick={() => handleOnClickSwitcher()}
          >
            <Icon size={0.7} icon={"SWAP_VERTICAL"} />
            <div>Switch</div>
          </Button>
        </div>
      </div>

      <div className="border-b border-zinc-300" />

      <div className="mx-auto px-4 py-5">
        <div className="flex flex-col gap-4">
          <TableLegacyContainer>
            <TableLegacy>
              <TableLegacyTHead>
                <TableLegacyTR>
                  <TableLegacyTH className={"sticky top-0"}>
                    User name
                  </TableLegacyTH>
                  <TableLegacyTH className={"sticky top-0"}>
                    Sellar Credit access
                  </TableLegacyTH>
                  <TableLegacyTH className={"sticky top-0"}>
                    Balance visible
                  </TableLegacyTH>
                </TableLegacyTR>
              </TableLegacyTHead>
              <TableLegacyTBody>
                {[
                  ...(currentBusinessUser ? [currentBusinessUser] : []),
                  ...allOtherUsers,
                ].map((businessUser) => {
                  const { name, email, profileImageUrl } = businessUser.User;
                  const isMe = businessUser.User.id === user.user?.id;

                  return (
                    <BusinessUser
                      key={businessUser.id}
                      businessUser={{
                        id: businessUser.id,
                        name,
                        email,
                        profileImageUrl,
                      }}
                      isMe={isMe}
                      canAccessSellarCredit={businessUser.canAccessSellarCredit}
                      canViewSellarCreditBalance={
                        businessUser.canViewSellarCreditBalance
                      }
                    />
                  );
                })}
              </TableLegacyTBody>
            </TableLegacy>
          </TableLegacyContainer>
        </div>
      </div>
    </PageContainer>
  );
};

const BusinessUser = ({
  businessUser,
  isMe,
  canAccessSellarCredit,
  canViewSellarCreditBalance,
}: {
  businessUser: {
    id: number;
    name: string | null;
    email: string;
    profileImageUrl: string | null;
  };
  isMe: boolean;
  canAccessSellarCredit: boolean;
  canViewSellarCreditBalance: boolean;
}) => {
  const businessUserUpdateMutation = useBusinessUserUpdate(businessUser.id);
  const [showConfirmUserAccessDialog, setShowConfirmUserAccessDialog] =
    useState(false);

  return (
    <>
      <TableLegacyTR>
        <TableLegacyTD>
          <div className={"flex items-center justify-start gap-3"}>
            <ProfilePicture
              name={businessUser.name}
              logoUrl={businessUser.profileImageUrl}
            />
            <div className="flex flex-col">
              <H5 className="break-words">
                {businessUser.name} {isMe && "(me)"}
              </H5>
              <H6 className="break-all select-text">
                {[businessUser.email].filter(Boolean).join(" - ")}
              </H6>
            </div>
          </div>
        </TableLegacyTD>
        <TableLegacyTD>
          <Switch
            disabled={isMe || businessUserUpdateMutation.isPending}
            checked={canAccessSellarCredit}
            onChange={() => {
              // allow user to toggle access off easily
              if (canAccessSellarCredit) {
                return businessUserUpdateMutation.mutateAsync({
                  canAccessSellarCredit: false,
                });
              }

              // show user access confirmation dialog
              setShowConfirmUserAccessDialog(true);
            }}
          />
        </TableLegacyTD>
        <TableLegacyTD>
          <Switch
            disabled={isMe || businessUserUpdateMutation.isPending}
            checked={canViewSellarCreditBalance}
            onChange={() => {
              businessUserUpdateMutation.mutateAsync({
                canViewSellarCreditBalance: !canViewSellarCreditBalance,
              });
            }}
          />
        </TableLegacyTD>
      </TableLegacyTR>
      <ConfirmUserAccessDialog
        businessUser={{
          name: businessUser.name ?? "",
          id: businessUser.id,
        }}
        active={showConfirmUserAccessDialog}
        onDismiss={() => {
          setShowConfirmUserAccessDialog(false);
        }}
      />
    </>
  );
};

const ManageBusinessesDialog = ({
  active,
  onDismiss,
  onClickContactSupport,
  legalEntity,
  businessesAttachedToLegalEntity,
}: {
  active: boolean;
  onDismiss: () => void;
  onClickContactSupport: () => void;
  legalEntity: { id: number; name: string };
  businessesAttachedToLegalEntity: number[];
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search.trim(), 300);
  const [ownershipConfirmation, setOwnershipConfirmation] = useState(false);
  const { user } = useSellarUser();
  const { user: firebaseUser } = useFirebaseUser();
  const { business: currentBusiness } = useSellarCurrentBusiness();

  const legalEntityUpdateMutation = useLegalEntityUpdate();
  const businessesQuery = useBusinessList(
    {
      query: [
        ...(user?.type !== "admin"
          ? [
              Object.freeze({
                type: "hasMembership",
                modifier: "EQUALS",
                value: true,
              }),
            ]
          : []),
        {
          type: "type",
          modifier: "EQUALS",
          value: "retailer",
        } as const,
      ],

      sort: [
        {
          direction: "asc",
          type: "type",
        },
        {
          direction: "asc",
          type: "name",
        },
      ],
      search: debouncedSearch,
      limit: 10,
    },
    {
      enabled: !!firebaseUser && !!user,
    },
  );

  const [selectedBusinessList, setSelectedBusinessesList] = useState(
    businessesAttachedToLegalEntity,
  );
  const [previousActive, setPreviousActive] = useState(active);
  const [previousBusinesses, setPreviousBusinesses] = useState(
    businessesAttachedToLegalEntity,
  );

  if (active !== previousActive) {
    setPreviousActive(active);
    setOwnershipConfirmation(false);
    setSearch("");
  }
  if (businessesAttachedToLegalEntity !== previousBusinesses) {
    setPreviousBusinesses(businessesAttachedToLegalEntity);
    setSelectedBusinessesList(businessesAttachedToLegalEntity);
  }

  const hasSelectedBusinessListChanged =
    [...businessesAttachedToLegalEntity].sort().join() !==
    [...selectedBusinessList].sort().join();

  const onUpdateBusinessesAttachedToLegalEntity = () => {
    legalEntityUpdateMutation
      .mutateAsync({ businessIdList: selectedBusinessList })
      .then(() => {
        onDismiss();
      })
      .catch((_error) => {
        console.error("Error updating legal entity attached businesses");
      });
  };

  return (
    <Dialog active={active} onDismiss={() => onDismiss()}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={() => onDismiss()}
        title={"Manage Businesses"}
        description={`Your Sellar Credit allowance can be used across all businesses owned by ${legalEntity.name}`}
      />
      <DialogContent className="">
        {legalEntityUpdateMutation.isError && (
          <Alert
            type="error"
            message={
              apiV2AssertError(legalEntityUpdateMutation.error)?.response?.data
                .message ?? "Unable to update businesses. Please try again."
            }
          />
        )}
        <div className="mb-2 text-sm font-medium text-zinc-800">
          Select which businesses are owned by {legalEntity.name}:
        </div>

        <InputField label="Search businesses" className="mb-4">
          <InputText
            type="search"
            placeholder="Search businesses by name"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </InputField>

        {businessesQuery.isPending && <SpinnerLegacy />}
        {businessesQuery.isError && (
          <div role="alert" className="mb-2 text-sm text-red-700">
            Unable to load businesses.
            <Button size="small" onClick={() => businessesQuery.refetch()}>
              Retry
            </Button>
          </div>
        )}
        {businessesQuery.isSuccess &&
          businessesQuery.data.pages.every((page) => page.length === 0) && (
            <p className="mb-2 text-sm text-zinc-600">No businesses found.</p>
          )}

        <div className="mb-2 max-h-[250px] overflow-auto">
          <div className="rounded-lg border border-zinc-300">
            <InfiniteList className={"w-full"} query={businessesQuery}>
              {businessesQuery.data &&
                businessesQuery.data.pages.map((page) =>
                  page.map((business) => {
                    const isLinkedElsewhere =
                      business.hasLegalEntity &&
                      !businessesAttachedToLegalEntity.includes(business.id);
                    return (
                      <div
                        key={business.id}
                        className="flex justify-between border-b border-zinc-300 p-4"
                      >
                        <div className="items-center text-sm font-semibold text-zinc-700">
                          {business.name}
                          {isLinkedElsewhere && (
                            <div className="text-sm font-normal text-zinc-500">
                              Already linked to another legal entity. Unlink it
                              there first.
                            </div>
                          )}
                        </div>
                        <input
                          aria-label={`Select ${business.name}`}
                          disabled={
                            business.id === currentBusiness?.id ||
                            (isLinkedElsewhere &&
                              !selectedBusinessList.includes(business.id)) ||
                            legalEntityUpdateMutation.isPending
                          }
                          checked={selectedBusinessList.includes(business.id)}
                          onChange={() => {
                            setSelectedBusinessesList((currentList) => {
                              if (currentList.includes(business.id)) {
                                return currentList.filter(
                                  (businessId) => businessId !== business.id,
                                );
                              }

                              return [...currentList, business.id];
                            });
                          }}
                          type={"checkbox"}
                        />
                      </div>
                    );
                  }),
                )}
            </InfiniteList>
          </div>
        </div>

        <div
          className="cursor-pointer text-center text-sm font-semibold text-zinc-800"
          onClick={() => onClickContactSupport()}
        >
          CAN'T SEE ALL YOUR BUSINESSES?
        </div>
      </DialogContent>

      <DialogCTA>
        <div className="flex flex-col items-end gap-4">
          {hasSelectedBusinessListChanged && (
            <label className="flex w-full cursor-pointer gap-4 rounded-lg border border-indigo-600 bg-indigo-50 p-4 text-left">
              <InputCheckbox
                variant="circular"
                checked={ownershipConfirmation}
                onChange={(event) =>
                  setOwnershipConfirmation(event.target.checked)
                }
                containerClassName="flex-none"
              />
              <p className="break-words">
                <p className="font-semibold">Ownership confirmation</p>I confirm
                that the businesses selected operate under the{" "}
                {legalEntity.name} registered Company name and take full
                responsibility for their usage of Sellar Credit
              </p>
            </label>
          )}

          <Button
            disabled={
              !hasSelectedBusinessListChanged ||
              (hasSelectedBusinessListChanged && !ownershipConfirmation) ||
              !legalEntity.id ||
              legalEntityUpdateMutation.isPending
            }
            loading={legalEntityUpdateMutation.isPending}
            onClick={() => onUpdateBusinessesAttachedToLegalEntity()}
          >
            Confirm
          </Button>
        </div>
      </DialogCTA>
    </Dialog>
  );
};

const ContactSupportDialog = ({
  active,
  onDismiss,
}: {
  active: boolean;
  onDismiss: () => void;
}) => {
  const intercom = useIntercom();

  return (
    <Dialog active={active} onDismiss={() => onDismiss()}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={() => onDismiss()}
        title={"Can't see all your businesses?"}
        description={
          "A member of our team can help you get set up on all your businesses on Sellar which are owned by your company."
        }
      />
      <DialogCTA className="flex justify-center">
        <Button
          className="flex min-w-fit gap-2"
          onClick={() => intercom.show()}
        >
          <Icon size={0.7} icon={"OPEN_IN_NEW"} />
          <div>CONTACT SUPPORT</div>
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

export const ConfirmUserAccessDialog = ({
  businessUser,
  onDismiss,
  active,
}: {
  businessUser: {
    name: string;
    id: number;
  };
  onDismiss: () => void;
  active: boolean;
}) => {
  const businessUserUpdateMutation = useBusinessUserUpdate(businessUser.id);

  return (
    <Dialog
      active={active}
      onDismiss={onDismiss}
      className="h-fit md:max-w-[480px]"
    >
      {businessUserUpdateMutation.isError && (
        <Alert
          type={"error"}
          message={
            apiV2AssertError(businessUserUpdateMutation.error)?.response?.data
              .message ?? "Unknown error"
          }
        />
      )}

      <DialogNavigation
        title={`Are you sure you want to turn on this user's access to Sellar Credit?`}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />

      <DialogContent className="flex items-center justify-center p-4 text-zinc-500">
        This will mean that {businessUser.name || "the user"} will be able to
        place orders using your Sellar credit tab
      </DialogContent>

      <DialogCTA>
        <Button
          className="w-full"
          onClick={() =>
            businessUserUpdateMutation
              .mutateAsync({
                canAccessSellarCredit: true,
                canViewSellarCreditBalance: true,
              })
              .then(() => onDismiss())
          }
          disabled={businessUserUpdateMutation.isPending}
        >
          Turn on Access
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

const InvoiceEmailDetails = ({
  legalEntityId,
  invoiceCcEmails,
  legalEntityAdminEmail,
}: {
  legalEntityId: number;
  invoiceCcEmails: string[];
  legalEntityAdminEmail: string;
}) => {
  const [openEditCCEmailsDialog, setOpenEditCCEmailsDialog] = useState(false);

  const invoiceMessage =
    invoiceCcEmails.length > 0 ? (
      invoiceCcEmails.join(", ")
    ) : (
      <span className="text-zinc-400">Not set</span>
    );

  return (
    <>
      <PageContainer className={"mx-auto max-w-300"}>
        <div className="mx-auto px-4 py-5">
          <div className="flex items-center justify-between gap-2">
            <div>
              <H5>Invoice and statement emails</H5>
              <div>
                Add the email addresses that should receive invoices,
                statements, and payment reminders
              </div>
            </div>

            <Button
              color="secondary"
              className="flex min-w-fit shrink-0 gap-2"
              onClick={() => setOpenEditCCEmailsDialog(true)}
            >
              <Icon size={0.7} icon={"PENCIL"} />
              <div>Edit</div>
            </Button>
          </div>
        </div>
        <div className="border-b border-zinc-300" />
        <div className="mx-auto px-4 py-5">
          <table className="w-full table-auto border-separate border-spacing-0 rounded-lg border border-zinc-300">
            <tr>
              <td className="border-r border-b border-zinc-300 px-4 py-3 md:w-50">
                <H5>TO:</H5>
              </td>
              <td className="border-b border-zinc-300 px-4 py-3">
                {legalEntityAdminEmail}
              </td>
            </tr>
            <tr>
              <td className="border-r border-zinc-300 px-4 py-3">
                <H5>CC:</H5>
              </td>
              <td className="px-4 py-3">{invoiceMessage}</td>
            </tr>
          </table>
        </div>
      </PageContainer>
      <InvoiceCcEmailUpdateDialog
        active={openEditCCEmailsDialog}
        invoiceCcEmails={invoiceCcEmails}
        legalEntityId={legalEntityId}
        onDismiss={() => setOpenEditCCEmailsDialog(false)}
      />
    </>
  );
};

type InvoiceCcEmailsUpdateDialogProps = {
  active: boolean;
  invoiceCcEmails: string[];
  legalEntityId: number;
  onDismiss: () => void;
};
const InvoiceCcEmailUpdateDialog = ({
  active,
  invoiceCcEmails,
  legalEntityId,
  onDismiss,
}: InvoiceCcEmailsUpdateDialogProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const legalEntityUpdateInvoiceCcEmailsMutation =
    useLegalEntityUpdateInvoiceCcEmails(legalEntityId);

  const form = useForm({
    defaultValues: {
      invoiceCcEmails: invoiceCcEmails.join(", "),
    },
    validators: {
      onChange: InvoiceCcEmailFormSchemaZ,
    },
    onSubmit: ({ value }) => {
      handleSubmit(InvoiceCcEmailFormSchemaZ.parse(value));
    },
  });

  const handleSubmit = (data: InvoiceCcEmailFormSchema) => {
    setErrorMessage(null);
    const invoiceCcEmailsToAdd = data.invoiceCcEmails
      .split(",")
      .map((email) => email.trim())
      .filter(Boolean);

    legalEntityUpdateInvoiceCcEmailsMutation.mutate(
      {
        invoiceCcEmails: invoiceCcEmailsToAdd,
      },
      {
        onSuccess: () => {
          handleDismiss();
        },
        onError: (error) => {
          const message = apiV2AssertError(error)?.response?.data.message ?? "";
          setErrorMessage(message);
        },
      },
    );
  };

  const handleDismiss = () => {
    setErrorMessage(null);
    form.reset();
    onDismiss();
  };

  const isUpdating = legalEntityUpdateInvoiceCcEmailsMutation.isPending;

  return (
    <Dialog active={active} onDismiss={handleDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={handleDismiss}
        title={"Update CC email addresses"}
        description={
          "Add the email addresses that should receive invoices, statements, and payment reminders."
        }
      />
      <DialogContent>
        <div className="flex flex-col gap-3">
          {errorMessage && (
            <Alert type="error" message={errorMessage} className="w-full" />
          )}

          <form.Field
            name="invoiceCcEmails"
            validators={{
              onBlur: ({ value }) => {
                const invalidEmails = value
                  .split(",")
                  .map((email) => email.trim())
                  .filter((email) => !isValidEmail(email))
                  .filter(Boolean);

                console.log("invalidEmails", invalidEmails);
                if (invalidEmails.length === 0) {
                  return undefined;
                }

                return `Please enter valid email addresses: ${invalidEmails.join(", ")}`;
              },
            }}
          >
            {({ state, handleChange, handleBlur }) => {
              const error = state.meta.errors[0];
              const errorMessage =
                typeof error === "string" ? error : error?.message;
              return (
                <div className="flex flex-col gap-1">
                  <InputLabel>Email addresses</InputLabel>
                  <InputContainer>
                    <InputTextArea
                      variant="legacyFixed"
                      rows={3}
                      className="h-18"
                      value={state.value}
                      disabled={isUpdating}
                      placeholder="accounts@example.com, sales@example.com, invoices@example.com"
                      onChange={(event) => {
                        const textContent = event.target.value;
                        handleChange(textContent);
                      }}
                      onBlur={handleBlur}
                    />
                  </InputContainer>
                  <p className="text-zinc-500">
                    To add multiple email addresses, separate them with a comma.
                  </p>
                  <FieldError errorMessage={errorMessage} />
                </div>
              );
            }}
          </form.Field>
        </div>
      </DialogContent>

      <DialogCTA>
        <Button
          type="button"
          onClick={() => {
            form.handleSubmit();
          }}
          loading={isUpdating}
          disabled={isUpdating}
        >
          Confirm
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

const InvoiceCcEmailFormSchemaZ = z.object({
  invoiceCcEmails: z.string(),
});

type InvoiceCcEmailFormSchema = z.infer<typeof InvoiceCcEmailFormSchemaZ>;

const FieldError = ({ errorMessage }: { errorMessage: string | undefined }) => {
  if (!errorMessage) {
    return null;
  }
  return (
    <span role="alert" className="text-sm text-red-700">
      {errorMessage}
    </span>
  );
};
